'use client';

import { faAdd } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Loader from '@pickleballinc/features/src/Loader/Loader';
import { withEfTid } from '@pickleballinc/lib/everflow/url';
import { LEAGUE_LABELS } from '@pickleballinc/lib/product-labels';
import { Session } from '@pickleballinc/lib/session';
import { Button } from '@pickleballinc/react-ui';
import { Url } from 'next/dist/shared/lib/router/router';
import { useEffect, useState } from 'react';

import { publicEnv } from '@/lib/env';
import { GetPBApproveToCreatePermissionRes } from '@/utils/actions/leagues/getPBApproveToCreatePermission';
import { EVERFLOW_DOMAINS } from '@/utils/helpers/constants';
import { getLoginUrl } from '@/utils/helpers/getLoginUrl';
import { isSsoURL } from '@/utils/helpers/isSsoURL';
import isValidURL from '@/utils/helpers/isValidURL';

interface TopButtonBarProps {
  user?: Session;
  createPerms: GetPBApproveToCreatePermissionRes;
}

export const TopButtonBar = ({ user, createPerms }: TopButtonBarProps) => {
  const [createANewLeagueURL, setCreateANewLeagueURL] = useState<Url>('');
  const [myLeaguesURL, setMyLeaguesURL] = useState('');
  const [myScheduleURL, setMyScheduleURL] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  // Create a League URL
  useEffect(() => {
    if (user?.uuid && createPerms?.ApprovedToCreate && createPerms.URLPath) {
      if (isValidURL(createPerms.URLPath)) {
        const url = new URL(createPerms.URLPath);
        url.searchParams.set('rurl', window.location.href);
        setCreateANewLeagueURL(url);
      } else {
        const url = new URL(`${publicEnv.NEXT_PUBLIC_PBRACKETS_URL}/${createPerms.URLPath}`);
        url.searchParams.set('rurl', window.location.href);
        setCreateANewLeagueURL(url);
      }
    } else {
      const url = new URL(publicEnv.NEXT_PUBLIC_CONTACT_FORM_URL);
      url.pathname += 'contacts/league/create';
      setCreateANewLeagueURL(url);
    }
  }, [createPerms, user]);

  const myLeaguesDestinationURL = new URL(`${publicEnv.NEXT_PUBLIC_PL_URL}/leagues/my-leagues`);
  const myScheduleDestinationURL = new URL(`${publicEnv.NEXT_PUBLIC_PL_URL}/leagues/my-schedule`);

  // My Leagues
  useEffect(() => {
    if (user?.uuid) {
      setMyLeaguesURL(myLeaguesDestinationURL.toString());
    } else {
      setMyLeaguesURL(getLoginUrl(myLeaguesDestinationURL.toString()));
    }
  }, [user]);

  // My Schedule
  useEffect(() => {
    if (user?.uuid) {
      setMyScheduleURL(myScheduleDestinationURL.toString());
    } else {
      setMyScheduleURL(getLoginUrl(myScheduleDestinationURL.toString()));
    }
  }, [user]);

  const handleNavigation = (url: string) => {
    setIsLoading(true);
    window.location.href = withEfTid(url, EVERFLOW_DOMAINS);
  };

  if (isLoading) {
    return (
      <div className='min-h-screen flex items-center justify-center'>
        <Loader backgroundColor='bg-leagues-300/90' />
      </div>
    );
  }

  return (
    <div className='container mx-auto grid grid-cols-2 gap-2 pb-2 sm:grid-cols-6 sm:items-center sm:gap-4 md:flex md:grid-cols-1 md:flex-row md:flex-wrap 2xl:px-0'>
      <div className='col-span-2 sm:col-span-3'>
        <a
          className='col-span-2 sm:col-span-3'
          href={myLeaguesURL}
          rel={isSsoURL(myLeaguesURL) ? 'nofollow' : undefined}
          aria-label={`Registered For a ${LEAGUE_LABELS.singular}`}
        >
          <Button
            variant='secondary'
            className='w-full justify-center !bg-leagues-300/10 font-bold !text-leagues-300'
            aria-label={`My ${LEAGUE_LABELS.plural}`}
          >
            {`My ${LEAGUE_LABELS.plural}`}
          </Button>
        </a>
      </div>

      <div className='col-span-2 sm:col-span-3'>
        <a
          className='col-span-2 sm:col-span-3'
          href={myScheduleURL}
          rel={isSsoURL(myScheduleURL) ? 'nofollow' : undefined}
          aria-label='Registered For a Game Days'
        >
          <Button
            variant='secondary'
            className='w-full justify-center !bg-leagues-300/10 font-bold !text-leagues-300'
            aria-label='My Schedule'
          >
            My Schedule
          </Button>
        </a>
      </div>

      {user?.uuid && createPerms?.ApprovedToCreate ? (
        <div className='col-span-2 sm:col-span-6 md:ml-auto'>
          <Button
            prefixIcon={<FontAwesomeIcon icon={faAdd} />}
            className='w-full justify-center !bg-leagues-300'
            variant='primary'
            onClick={() => handleNavigation(createANewLeagueURL.toString())}
            aria-label={`Create a New ${LEAGUE_LABELS.singular}`}
          >
            Create a New {LEAGUE_LABELS.singular}
          </Button>
        </div>
      ) : null}
    </div>
  );
};
